import React, { useMemo } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Autocomplete, Button, Paper, Stack, TextField } from "@mui/material";
import { FolderPathDisplay } from "components/modals/Files-upload/FolderPathDisplay";
import { CreateDealContent } from "components/Prompts/CreateDealContent";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useCanCreateDeal, usePrompt } from "hooks";
import { DealBase, IFolder } from "store/types";

export const GLOBAL_WORKSPACE_DEAL: DealBase = { id: -1, title: "Global Workspace" };

interface Props {
  deal: DealBase | null;
  folderId: number | null;
  disableDealSelect?: boolean;
  disableFolderSelect?: boolean;
  folders: IFolder[];
  foldersLoading: boolean;
  dealOptions: DealBase[];
  setDeal: (deal: DealBase | null) => void;
  onSelectFolderClick: () => Promise<void>;
}

export const TargetSelect: React.FC<Props> = ({
  deal,
  folderId,
  disableDealSelect,
  disableFolderSelect,
  folders,
  foldersLoading,
  dealOptions,
  setDeal,
  onSelectFolderClick
}) => {
  const canCreateDeal = useCanCreateDeal();
  const openPrompt = usePrompt();

  const options = useMemo(() => [GLOBAL_WORKSPACE_DEAL, ...dealOptions], [dealOptions]);

  const handleCreateDeal = async (e: React.MouseEvent) => {
    e.preventDefault();
    const data = await openPrompt({ Component: CreateDealContent });
    if (!data) return;
    setDeal({ id: data.dealId, title: data.dealTitle });
  };

  return (
    <>
      <Stack direction="row" alignItems="center" gap="14px" flexWrap="wrap">
        <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            sx={{
              maxWidth: 170,
              "& .MuiOutlinedInput-root": {
                height: 32,
                minHeight: 32,
                "& .MuiInputBase-input": { py: 0 }
              }
            }}
            fullWidth
            disabled={disableDealSelect}
            options={options}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            PaperComponent={({ children }) => (
              <Paper>
                {children}
                {!disableDealSelect && canCreateDeal && (
                  <>
                    <Button
                      onMouseDown={handleCreateDeal}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Add Deal
                    </Button>
                  </>
                )}
              </Paper>
            )}
            renderInput={(params) => <TextField {...params} label="Deal" required />}
            value={deal ? options.find((opt) => opt.id === deal.id) : null}
            onChange={(_, value) => setDeal(value)}
          />
        </Stack>
        <Stack flexDirection="row" alignItems="center">
          {!deal && (
            <MaterialTooltip isShow title="Please select a deal to continue">
              <InfoOutlinedIcon sx={{ fontSize: 20, color: "#92A7BC" }} />
            </MaterialTooltip>
          )}
          <FolderPathDisplay
            folders={folders}
            folderId={folderId}
            foldersLoading={foldersLoading}
            disabled={!deal}
            action={!disableFolderSelect && deal ? onSelectFolderClick : undefined}
          />
        </Stack>
      </Stack>
    </>
  );
};
